import { useListStore } from "../../store/listStore";
import { usePlaceStore } from "../../store/placeStore";
import { useAuthStore } from "../../store/authStore";
import { fetchGooglePlaceDetails } from "../../api/use-get-google-place-details";
import { PlacePrediction } from "../../api/use-post-google-places-autocomplete";
import { UniversalSearch } from "../UniversalSearch";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import LockIcon from "@mui/icons-material/Lock";
import PublicIcon from "@mui/icons-material/Public";
import SentimentDissatisfiedIcon from "@mui/icons-material/SentimentDissatisfied";
import {
  Box,
  Button,
  Divider,
  IconButton,
  Input,
  List,
  ListItemButton,
  Modal,
  ModalClose,
  ModalDialog,
  Stack,
  Textarea,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/joy";
import React from "react";

type EditListProps = {
  listId: string;
  onPlaceClick?: (placeId: string, listId: string) => void;
  onDelete?: () => void;
};

export const EditList = ({ listId, onPlaceClick, onDelete }: EditListProps) => {
  const theme = useTheme();

  const { lists, updateList, deleteList } = useListStore();
  const { listPlaces, fetchListPlaces, deleteListPlace } = usePlaceStore();
  const list = lists.find((list) => list.id === listId);

  const [title, setTitle] = React.useState(list?.title || "");
  const [description, setDescription] = React.useState(list?.description || "");
  const [isEditingTitle, setIsEditingTitle] = React.useState(false);
  const [isEditingDescription, setIsEditingDescription] = React.useState(false);
  const [prevListId, setPrevListId] = React.useState(listId);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const [isEditingPlaces, setIsEditingPlaces] = React.useState(false);
  const [isQuickAdding, setIsQuickAdding] = React.useState(false);
  const { user } = useAuthStore();
  const { createPlace, addPlaceToList } = usePlaceStore();

  // Reset form when list changes (recommended React pattern for syncing with props)
  if (list && listId !== prevListId) {
    setPrevListId(listId);
    setTitle(list.title);
    setDescription(list.description || "");
  }

  React.useEffect(() => {
    if (list) {
      fetchListPlaces(list.id);
    }
  }, [list, fetchListPlaces]);

  if (!list) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        marginTop={theme.spacing(10)}
      >
        <SentimentDissatisfiedIcon
          fontSize="large"
          data-testid="list-not-found-icon"
        />
        <Typography>Oops, list not found!</Typography>
      </Box>
    );
  }

  const handleDeleteConfirm = async () => {
    try {
      await deleteList(list!.id);
      onDelete?.();
    } catch {
      // error is already captured in store
    } finally {
      setDeleteConfirmOpen(false);
    }
  };

  const saveName = () => {
    updateList(list.id, { title });
    setIsEditingTitle(false);
  };

  const saveDescription = () => {
    updateList(list.id, { description });
    setIsEditingDescription(false);
  };

  const handleQuickAddPlace = async (place: PlacePrediction | null) => {
    if (!place || !user) return;
    try {
      const details = await fetchGooglePlaceDetails(place.placeId);
      const country = details.addressComponents?.find((c) =>
        c.types.includes("country"),
      )?.longText;
      const newPlace = await createPlace({
        user_id: user.id,
        name: details.displayName.text,
        address: details.formattedAddress,
        latitude: details.location.latitude,
        longitude: details.location.longitude,
        google_place_id: place.placeId,
        country: country || null,
      });
      await addPlaceToList(newPlace, list.id);
      fetchListPlaces(list.id);
    } catch {
      // error is captured in store
    }
  };

  return (
    <Stack spacing={1}>
      <Stack paddingLeft={theme.spacing(2)} paddingRight={theme.spacing(2)}>
        <Stack direction="row" alignItems="center">
          {isEditingTitle ? (
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={saveName}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === "Escape") {
                  saveName();
                }
              }}
              autoFocus
              variant="soft"
              size="md"
              placeholder="Untitled List"
              data-testid="list-title"
              sx={{
                flex: 1,
                padding: `${theme.spacing(1.25)} ${theme.spacing(1)}`,
                fontWeight: theme.fontWeight.lg,
              }}
            />
          ) : (
            <Button
              onClick={() => setIsEditingTitle(true)}
              variant="plain"
              color="neutral"
              size="lg"
              data-testid="list-title"
              sx={{
                flex: 1,
                padding: theme.spacing(1),
                justifyContent: "flex-start",
                fontWeight: theme.fontWeight.lg,
              }}
            >
              {title}
            </Button>
          )}
          <IconButton
            size="sm"
            variant="plain"
            color="danger"
            aria-label="Delete list"
            data-testid="delete-list-button"
            onClick={() => setDeleteConfirmOpen(true)}
          >
            <DeleteIcon />
          </IconButton>
        </Stack>
        {/* <Typography level="body-sm" paddingLeft={theme.spacing(1)}>
          {list.places.length} place{list.places.length !== 1 && "s"}
        </Typography> */}
        {isEditingDescription ? (
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onBlur={saveDescription}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                saveDescription();
              }
            }}
            autoFocus
            size="sm"
            placeholder="Add a list description"
            data-testid="list-description"
            sx={{
              padding: `${theme.spacing(0.9)} ${theme.spacing(0.9)}`,
            }}
          />
        ) : (
          <Button
            onClick={() => setIsEditingDescription(true)}
            variant="plain"
            color="neutral"
            data-testid="list-description"
            sx={{
              whiteSpace: "pre-line",
              textAlign: "start",
              padding: theme.spacing(1),
              justifyContent: "flex-start",
              color: description ? "text.primary" : "text.tertiary",
              fontWeight: theme.fontWeight.sm,
            }}
          >
            {description || "Add a list description"}
          </Button>
        )}
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Tooltip
            title={
              list.privacy_level === "public"
                ? "Click to make private"
                : "Click to make public"
            }
            placement="bottom-start"
          >
            <Button
              variant="plain"
              color="neutral"
              size="sm"
              startDecorator={
                list.privacy_level === "public" ? (
                  <PublicIcon fontSize="small" />
                ) : (
                  <LockIcon fontSize="small" />
                )
              }
              onClick={() =>
                updateList(list.id, {
                  privacy_level:
                    list.privacy_level === "public" ? "private" : "public",
                })
              }
              data-testid="list-privacy-toggle"
              sx={{ paddingLeft: 0, marginTop: theme.spacing(0.5) }}
            >
              {list.privacy_level === "public" ? "Public" : "Private"}
            </Button>
          </Tooltip>
          <Stack direction="row" spacing={0.5}>
            <Tooltip
              title={isQuickAdding ? "Close quick add" : "Quick add place"}
              placement="bottom-end"
            >
              <IconButton
                size="sm"
                variant={isQuickAdding ? "soft" : "plain"}
                color="neutral"
                onClick={() => setIsQuickAdding((v) => !v)}
                data-testid="quick-add-toggle"
                aria-label={isQuickAdding ? "Close quick add" : "Quick add place"}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip
              title={isEditingPlaces ? "Done editing" : "Edit places"}
              placement="bottom-end"
            >
              <IconButton
                size="sm"
                variant={isEditingPlaces ? "soft" : "plain"}
                color="neutral"
                onClick={() => setIsEditingPlaces((v) => !v)}
                data-testid="edit-places-toggle"
                aria-label={isEditingPlaces ? "Done editing places" : "Edit places"}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Stack>
      <Divider />
      {isQuickAdding && (
        <Box sx={{ px: 2, py: 1 }}>
          <UniversalSearch
            searchTypes="places"
            onSelectPlace={handleQuickAddPlace}
            sx={{ position: "relative" }}
            hideSeeAllResults
            listboxZIndex={1200}
          />
        </Box>
      )}
      <List>
        {listPlaces.map((lp) => (
          <ListItemButton
            key={lp.id}
            sx={{ padding: `${theme.spacing(1)} ${theme.spacing(3)}` }}
            onClick={() => onPlaceClick?.(lp.id, listId)}
            data-testid={`place-item-${lp.id}`}
          >
            <Box sx={{ flex: 1 }}>{lp.place_name}</Box>
            {isEditingPlaces && (
              <IconButton
                size="sm"
                variant="plain"
                color="danger"
                onClick={(e) => {
                  e.stopPropagation();
                  deleteListPlace(lp.id);
                }}
                data-testid={`delete-place-button-${lp.id}`}
                aria-label={`Remove ${lp.place_name}`}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </ListItemButton>
        ))}
      </List>

      <Modal
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        data-testid="delete-list-confirm-modal"
      >
        <ModalDialog variant="outlined" role="alertdialog">
          <ModalClose />
          <Typography level="h4">Delete List</Typography>
          <Typography level="body-md">
            Are you sure you want to delete this list? This action cannot be
            undone.
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="flex-end" marginTop={2}>
            <Button
              variant="plain"
              color="neutral"
              onClick={() => setDeleteConfirmOpen(false)}
              data-testid="delete-list-cancel-button"
            >
              Cancel
            </Button>
            <Button
              variant="solid"
              color="danger"
              onClick={handleDeleteConfirm}
              data-testid="delete-list-confirm-button"
            >
              Delete
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>
    </Stack>
  );
};
